<template>
  <div class="employee-management inv-compact-page">
    <!-- 顶部：标题 + 时间筛选 -->
    <div class="card-header" style="margin-bottom: 10px;">
      <div class="card-title">库存仪表盘</div>

      <div class="time-filter">
        <div class="time-filter-buttons">
        <el-button
          :type="rangeType === 'today' ? 'primary' : 'default'"
          :class="rangeType === 'today' ? 'btn-primary' : 'btn-secondary'"
          size="small"
          @click="rangeType = 'today'"
          >
            今日
          </el-button>
          <el-button
            :type="rangeType === 'week' ? 'primary' : 'default'"
            :class="rangeType === 'week' ? 'btn-primary' : 'btn-secondary'"
            size="small"
            @click="rangeType = 'week'"
          >
            本周
          </el-button>
          <el-button
            :type="rangeType === 'month' ? 'primary' : 'default'"
            :class="rangeType === 'month' ? 'btn-primary' : 'btn-secondary'"
            size="small"
            @click="rangeType = 'month'"
          >
            本月
          </el-button>
          <el-button
            :type="rangeType === 'custom' ? 'primary' : 'default'"
            :class="rangeType === 'custom' ? 'btn-primary' : 'btn-secondary'"
            size="small"
            @click="rangeType = 'custom'"
          >
            自定义
          </el-button>
        </div>

        <div class="time-filter-excel-wrap">
          <div class="dashboard-excel-actions inv-mgmt-excel-actions">
            <el-upload
              class="el-upload el-upload--button inv-mgmt-upload"
              :show-file-list="false"
              :before-upload="handleImportExcel"
              accept=".xls,.xlsx"
            >
              <el-button type="default" size="small" class="inv-mgmt-excel-btn">
                导入Excel
              </el-button>
            </el-upload>

            <el-button type="default" size="small" class="inv-mgmt-excel-btn" @click="exportExcel">
              导出Excel
            </el-button>

            <a class="inv-mgmt-template-link" href="javascript:;" @click.prevent="handleDownloadTemplate">下载模板</a>
          </div>
        </div>

        <el-date-picker
          v-if="rangeType === 'custom'"
          v-model="customRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          align="right"
          size="small"
          style="margin-left: 12px;"
          @change="fetchStats"
        />
      </div>
    </div>

    <!-- 核心数据卡片区 -->
    <div class="cards-row">
      <div class="stat-card">
        <div class="stat-label">总库存数量</div>
        <div class="stat-value" :style="{ color: brandBlue }">{{ stats.totalStock }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">{{ rangeType === 'today' ? '今日' : '区间' }}入库数</div>
        <div class="stat-value" :style="{ color: brandPrimary }">{{ stats.inboundCount }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">{{ rangeType === 'today' ? '今日' : '区间' }}出库数</div>
        <div class="stat-value" :style="{ color: brandOrange }">{{ stats.outboundCount }}</div>
      </div>
    </div>

    <!-- 库存明细列表：筛选 + 表格 -->
    <div class="section">
      <div class="section-header">
        <div class="section-title">库存明细</div>
        <div class="filters">
          <div class="filters-left">
          <el-select v-model="filters.region" placeholder="使用区域" size="small" style="width: 140px;" @change="fetchRows">
            <el-option label="全部" value="all" />
            <el-option v-for="r in regions" :key="r" :label="r" :value="r" />
          </el-select>
          <el-input
            v-model="filters.keyword"
            placeholder="商品名称 / 条形码"
            size="small"
            clearable
            style="width: 200px; margin-left: 8px;"
            prefix-icon="el-icon-search"
            @keyup.enter.native="applyFilters"
          />
          </div>

          <div class="filters-actions">
            <el-button type="primary" size="small" icon="el-icon-search" @click="applyFilters">筛选</el-button>
            <el-button size="small" style="margin-left: 8px;" @click="resetFilters">重置</el-button>
          </div>
        </div>
      </div>

      <el-table
        :data="rows"
        border
        stripe
        size="small"
        style="width: 100%;"
        class="employee-table"
      >
        <el-table-column prop="productName" label="商品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="barcode" label="条形码" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.barcode || '—' }}</template>
        </el-table-column>
        <el-table-column prop="usageArea" label="区域" width="100" />
        <el-table-column prop="unit" label="单位" width="64" />
        <el-table-column prop="currentStock" label="当前库存" width="90" />
        <el-table-column prop="warningThreshold" label="最低阈值" width="90" />
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button size="small" class="btn-secondary" @click="viewDetail(scope.row)">
              查看详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 图表区 -->
    <div class="section charts-section">
      <div class="charts-header">
        <div class="section-title">库存可视化（按使用区域）</div>
      </div>

      <el-row :gutter="20">
        <el-col :span="16">
          <div class="chart-card">
            <div class="chart-title">库存趋势折线图（近30天）</div>
            <svg class="line-chart" viewBox="0 0 700 260" preserveAspectRatio="none">
              <g transform="translate(60,20)">
                <line x1="0" y1="220" x2="620" y2="220" stroke="var(--border-color)" />
                <line x1="0" y1="0" x2="0" y2="220" stroke="var(--border-color)" />

                <g v-for="(_, idx) in 5" :key="idx">
                  <line :x1="0" :y1="idx * 44" :x2="620" :y2="idx * 44" stroke="var(--tag-bg)" />
                </g>

                <path v-if="linePath.inbound" :d="linePath.inbound" fill="none" :stroke="brandPrimary" stroke-width="2.6" />
                <path v-if="linePath.outbound" :d="linePath.outbound" fill="none" :stroke="brandOrange" stroke-width="2.6" />
                <path v-if="linePath.net" :d="linePath.net" fill="none" stroke-dasharray="4 3" :stroke="brandBlue" stroke-width="2.2" />

                <circle
                  v-for="(p, i) in linePoints.inbound"
                  :key="'in' + i"
                  :cx="p.x"
                  :cy="p.y"
                  r="3.5"
                  fill="var(--card-bg)"
                  :stroke="brandPrimary"
                  stroke-width="2"
                />
                <circle
                  v-for="(p, i) in linePoints.outbound"
                  :key="'out' + i"
                  :cx="p.x"
                  :cy="p.y"
                  r="3.5"
                  fill="var(--card-bg)"
                  :stroke="brandOrange"
                  stroke-width="2"
                />
              </g>
            </svg>

            <div class="chart-legend">
              <span><i class="legend-dot" :style="{ background: brandPrimary }"></i>入库</span>
              <span><i class="legend-dot" :style="{ background: brandOrange }"></i>出库</span>
              <span><i class="legend-dot" :style="{ background: brandBlue, borderColor: brandBlue }"></i>库存变化</span>
            </div>
          </div>
        </el-col>

        <el-col :span="8">
          <div class="chart-card">
            <div class="chart-title">库存数量占比（吧台库存 / 后厨库存 / 其他库存）</div>
            <div class="pie-wrap">
              <svg class="pie-chart" viewBox="0 0 280 200">
                <g transform="translate(120,100)">
                  <path
                    v-for="(seg, i) in pieSegments"
                    :key="'seg' + i"
                    :d="seg.d"
                    :fill="seg.color"
                    stroke="#ffffff"
                    stroke-width="1"
                  />
                </g>
              </svg>
              <div class="pie-legend">
                <div v-for="(item, idx) in pieLegend" :key="'pleg' + idx" class="pie-legend-item">
                  <i class="legend-dot" :style="{ background: pieColors[idx % pieColors.length] }"></i>
                  <span class="pie-legend-name">{{ item.name }}</span>
                  <span class="pie-legend-val">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 查看详情弹窗 -->
    <el-dialog title="库存详情" :visible.sync="detailDialogVisible" width="640px">
      <div v-if="detailRow">
        <div class="detail-top">
          <div class="detail-name">{{ detailRow.productName }}</div>
          <div class="detail-meta">
            <span>使用区域：{{ detailRow.usageArea || detailRow.region }}</span>
            <span>单位：{{ detailRow.unit || '—' }}</span>
          </div>
        </div>

        <div class="detail-cards">
          <div class="detail-card">
            <div class="detail-label">当前库存</div>
            <div class="detail-value">{{ detailRow.currentStock }}</div>
          </div>
          <div class="detail-card">
            <div class="detail-label">最低阈值</div>
            <div class="detail-value">{{ detailRow.warningThreshold != null && detailRow.warningThreshold !== '' ? detailRow.warningThreshold : '—' }}</div>
          </div>
        </div>

        <el-table :data="detailLogs" border stripe style="width: 100%; margin-top: 14px;">
          <el-table-column prop="operationTime" label="操作时间" min-width="180" />
          <el-table-column prop="productName" label="商品名称" min-width="150" />
          <el-table-column prop="logTypeLabel" label="类型" min-width="130" />
          <el-table-column prop="quantityDisplay" label="数量" min-width="110" />
          <el-table-column prop="operator" label="操作人" min-width="120" />
          <el-table-column prop="remark" label="备注" min-width="160" />
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchInventoryRegions,
  fetchInventoryRows,
  fetchInventoryStats,
  fetchInventoryVisualization,
  fetchInventoryLogs,
  downloadInventoryTemplate,
  exportInventoryExcel,
  importInventoryExcel
} from '../api/inventory'
import { onInventoryChanged, notifyInventoryChanged } from '../utils/inventorySync'
import { guardUploadFile, formatImportErrors as formatImportErrorsText, showImportResult } from '../utils/fileImport'

const SYNC_SOURCE = 'InventoryDashboard'

export default {
  name: 'InventoryDashboard',
  data() {
    return {
      _unsubInventorySync: null,
      brandPrimary: 'var(--primary-color)',
      brandBlue: 'var(--text-subtitle)',
      brandOrange: 'var(--secondary-color)',

      rangeType: 'today',
      customRange: [],

      regions: [],
      filters: {
        region: 'all',
        keyword: ''
      },

      stats: {
        totalSku: 0,
        totalStock: 0,
        lowStockCount: 0,
        inboundCount: 0,
        outboundCount: 0,
        countByArea: {},
        monthInboundByArea: {},
        monthOutboundByArea: {}
      },

      rows: [],
      viz: null,

      detailDialogVisible: false,
      detailRow: null,
      detailLogs: [],

      pieColors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--text-subtitle)']
    }
  },
  computed: {
    areaSkuHint() {
      const c = this.stats && this.stats.countByArea
      if (!c || typeof c !== 'object') return ''
      const parts = (this.regions || []).map((a) => `${a} ${c[a] != null ? c[a] : 0} 种`)
      return parts.length ? parts.join(' · ') : ''
    },
    monthInHint() {
      const m = this.stats && this.stats.monthInboundByArea
      if (!m || typeof m !== 'object') return ''
      const parts = (this.regions || []).map((a) => `${a} +${m[a] != null ? m[a] : 0}`)
      return parts.length ? `本月入库：${parts.join(' · ')}` : ''
    },
    monthOutHint() {
      const m = this.stats && this.stats.monthOutboundByArea
      if (!m || typeof m !== 'object') return ''
      const parts = (this.regions || []).map((a) => `${a} -${m[a] != null ? m[a] : 0}`)
      return parts.length ? `本月出库：${parts.join(' · ')}` : ''
    },
    pieLegend() {
      if (!this.viz || !this.viz.pie) return []
      return this.viz.pie.byRegion || this.viz.pie.byCategory || []
    },
    pieSegments() {
      if (!this.pieLegend || !this.pieLegend.length) return []
      const total = this.pieLegend.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1
      const cx = 0
      const cy = 0
      const r = 60
      let angle = -90
      return this.pieLegend.map((item, idx) => {
        const val = Number(item.value) || 0
        const delta = (val / total) * 360
        const startAngle = angle
        const endAngle = angle + delta
        angle = endAngle
        const d = this.describeArc(cx, cy, r, startAngle, endAngle)
        return { d, color: this.pieColors[idx % this.pieColors.length] }
      })
    },

    linePoints() {
      if (!this.viz || !this.viz.line) return { inbound: [], outbound: [], net: [] }
      const w = 620
      const h = 220
      const padding = 0
      const maxVal = Math.max(
        ...this.viz.line.inbound,
        ...this.viz.line.outbound,
        ...this.viz.line.netChange.map(v => Math.abs(v))
      ) || 1
      const n = this.viz.line.dates.length
      const step = n <= 1 ? 0 : w / (n - 1)

      const toPoint = (arr) => arr.map((v, i) => {
        const x = i * step
        const val = Number(v) || 0
        const y = h - (Math.abs(val) / maxVal) * h
        return { x, y }
      })

      return {
        inbound: toPoint(this.viz.line.inbound),
        outbound: toPoint(this.viz.line.outbound),
        net: toPoint(this.viz.line.netChange)
      }
    },
    linePath() {
      const mk = (points) => points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ')
      return {
        inbound: this.linePoints.inbound.length ? mk(this.linePoints.inbound) : '',
        outbound: this.linePoints.outbound.length ? mk(this.linePoints.outbound) : '',
        net: this.linePoints.net.length ? mk(this.linePoints.net) : ''
      }
    }
  },
  watch: {
    rangeType() {
      this.fetchStats()
    }
  },
  mounted() {
    this.init()
    this._unsubInventorySync = onInventoryChanged((e) => {
      const src = e && e.detail && e.detail.source
      if (src === SYNC_SOURCE) return
      this.refreshInventoryFromSync()
    })
  },
  beforeDestroy() {
    if (this._unsubInventorySync) {
      this._unsubInventorySync()
      this._unsubInventorySync = null
    }
  },
  methods: {
    async refreshInventoryFromSync() {
      try {
        await Promise.all([
          this.fetchStats(),
          this.fetchRows(),
          this.fetchVisualization()
        ])
        if (this.detailDialogVisible && this.detailRow) {
          await this.viewDetail(this.detailRow)
        }
      } catch (e) {}
    },
    async init() {
      try {
        const regionsResp = await fetchInventoryRegions()
        this.regions = regionsResp && regionsResp.data ? regionsResp.data : []

        await this.fetchStats()
        await this.fetchRows()
        await this.fetchVisualization()
      } catch (e) {
        this.regions = []
        this.stats = {
          totalSku: 0,
          totalStock: 0,
          lowStockCount: 0,
          inboundCount: 0,
          outboundCount: 0,
          countByArea: {},
          monthInboundByArea: {},
          monthOutboundByArea: {}
        }
        this.rows = []
        this.viz = null
      }
    },

    async fetchStats() {
      const startDate = this.rangeType === 'custom' && this.customRange[0] ? this.customRange[0].toISOString() : ''
      const endDate = this.rangeType === 'custom' && this.customRange[1] ? this.customRange[1].toISOString() : ''
      try {
        const resp = await fetchInventoryStats({ rangeType: this.rangeType, startDate, endDate })
        if (resp && resp.code === 200 && resp.data) {
          this.stats = resp.data
        }
      } catch (e) {}
    },

    async fetchRows() {
      try {
        const resp = await fetchInventoryRows(this.filters)
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.rows = resp.data
        } else if (resp && Array.isArray(resp.data)) {
          this.rows = resp.data
        } else {
          this.rows = []
        }
      } catch (e) {
        this.rows = []
      }
    },

    applyFilters() {
      // 仅触发已有的拉取逻辑，不改数据口径
      this.fetchRows()
    },

    resetFilters() {
      this.filters = { region: 'all', keyword: '' }
      this.fetchRows()
    },

    async handleDownloadTemplate() {
      try {
        await downloadInventoryTemplate()
      } catch (e) {
        this.$message.error('模板下载失败')
      }
    },

    async fetchVisualization() {
      try {
        const resp = await fetchInventoryVisualization(30)
        if (resp && resp.code === 200 && resp.data) {
          this.viz = resp.data
        } else {
          this.viz = null
        }
      } catch (e) {
        this.viz = null
      }
    },

    async exportExcel() {
      try {
        await exportInventoryExcel({
          region: this.filters.region,
          keyword: this.filters.keyword
        })
      } catch (e) {
        this.$message.error('导出失败')
      }
    },

    formatImportErrors(result) {
      return formatImportErrorsText(result)
    },

    async handleImportExcel(file) {
      if (!guardUploadFile(this, file)) return false
      try {
        const resp = await importInventoryExcel(file, '管理员')
        if (resp && resp.code === 200) {
          showImportResult(this, resp, { successMessage: '导入成功' })
          await this.fetchStats()
          await this.fetchRows()
          await this.fetchVisualization()
          notifyInventoryChanged(SYNC_SOURCE)
        } else if (resp && resp.code === 400 && resp.data && resp.data.errors) {
          this.$alert(formatImportErrorsText(resp.data) || resp.message || '导入失败', '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((resp && resp.message) || '导入失败')
        }
      } catch (e) {
        const data = e && e.response && e.response.data
        if (data && (data.message || (data.data && data.data.errors))) {
          const msg = formatImportErrorsText(data.data) || data.message
          this.$alert(msg, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((data && data.message) || '导入失败')
        }
      }
      return false
    },

    describeArc(cx, cy, r, startAngle, endAngle) {
      const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0
        return {
          x: centerX + (radius * Math.cos(angleInRadians)),
          y: centerY + (radius * Math.sin(angleInRadians))
        }
      }
      const start = polarToCartesian(cx, cy, r, endAngle)
      const end = polarToCartesian(cx, cy, r, startAngle)
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

      return [
        'M', cx, cy,
        'L', start.x, start.y,
        'A', r, r, 0, largeArcFlag, 0, end.x, end.y,
        'Z'
      ].join(' ')
    },

    async viewDetail(row) {
      this.detailRow = { ...row }
      this.detailDialogVisible = true
      this.detailLogs = []

      const reg = row.usageArea || row.region || 'all'
      const kw = row.productName || ''
      try {
        const [inResp, outResp] = await Promise.all([
          fetchInventoryLogs({ type: 'in', region: reg, keyword: kw, page: 1, pageSize: 50 }),
          fetchInventoryLogs({ type: 'out', region: reg, keyword: kw, page: 1, pageSize: 50 })
        ])
        const inList = inResp && inResp.code === 200 && inResp.data ? inResp.data.list : []
        const outList = outResp && outResp.code === 200 && outResp.data ? outResp.data.list : []

        this.detailLogs = [...inList, ...outList].sort((a, b) => (a.operationTime < b.operationTime ? 1 : -1))
      } catch (e) {
        this.detailLogs = []
      }
    }
  }
}
</script>

<style scoped>
.inventory-dashboard-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 700;
  color: #1d2129;
}

.time-filter {
  display: flex;
  align-items: center;
  flex: 1;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  min-width: 0;
}

.time-filter-excel-wrap {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  min-width: 200px;
}

.dashboard-excel-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.time-filter-buttons {
  display: flex;
  align-items: center;
  margin-right: 12px;
}

.time-filter-buttons .el-button + .el-button {
  margin-left: 10px;
}

.time-filter-buttons .el-button {
  height: 32px;
  line-height: 32px;
  padding-top: 0;
  padding-bottom: 0;
}

/* 库存可视化模式切换按钮：统一高度 */
.pie-mode .el-button {
  height: 32px;
  line-height: 32px;
  padding-top: 0;
  padding-bottom: 0;
}

/* 让“库存明细”卡片边角更圆润（仅影响本页表格） */
.employee-table ::v-deep(.el-table--border) {
  border-radius: 12px;
  overflow: hidden;
}

/* 仪表盘内所有按钮：统一高度（与时间筛选按钮一致） */
.employee-management ::v-deep .el-button {
  height: 32px;
  line-height: 32px;
  padding-top: 0;
  padding-bottom: 0;
}

/* 导入/导出内边距由 App.vue 全局 .inv-mgmt-excel-btn 统一为 12px */
.employee-management ::v-deep .el-button.inv-mgmt-excel-btn {
  height: auto !important;
  line-height: 1.4 !important;
}

/* 库存明细下方图表卡片：保证圆角裁切 */
.charts-section .chart-card {
  border-radius: 12px;
  overflow: hidden;
}

.employee-table ::v-deep(.el-table__header-wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.employee-table ::v-deep(.el-table__body-wrapper) {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.cards-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.stat-card {
  flex: 1;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 12px 14px;
}

.cards-row > .stat-card:nth-child(3) {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.stat-label {
  font-size: 14px;
  color: #4e5969;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
}

.stat-sub {
  margin-top: 10px;
  font-size: 12px;
  color: #86909c;
  line-height: 1.5;
}

.section {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 10px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #1d2129;
}

.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.filters-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.filters-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.charts-section {
  margin-top: 18px;
}

.charts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.chart-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #1d2129;
  margin-bottom: 10px;
}

.line-chart {
  width: 100%;
  height: 240px;
  background: #fff;
}

.chart-legend {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 12px;
  color: #4e5969;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  border: 2px solid transparent;
}

.pie-wrap {
  display: flex;
  gap: 12px;
}

.pie-chart {
  width: 130px;
  height: 130px;
  flex: 0 0 auto;
}

.pie-legend {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.pie-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4e5969;
}

.pie-legend-name {
  flex: 1;
  min-width: 70px;
}

.pie-legend-val {
  color: #4e5969;
}

.detail-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-name {
  font-size: 16px;
  font-weight: 800;
  color: #1d2129;
}

.detail-meta {
  display: flex;
  gap: 16px;
  color: #4e5969;
  font-size: 13px;
}

.detail-cards {
  display: flex;
  gap: 14px;
  margin-top: 14px;
}

.detail-card {
  flex: 1;
  background: #f9fafb;
  border-radius: 12px;
  padding: 14px 12px;
}

.detail-label {
  font-size: 12px;
  color: #4e5969;
  margin-bottom: 8px;
}

.detail-value {
  font-size: 22px;
  font-weight: 800;
  color: #1d2129;
}

@media (max-width: 980px) {
  .cards-row {
    flex-direction: column;
  }
  .pie-wrap {
    flex-direction: column;
  }
}
</style>

